import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthAPIService } from '@services/AuthService/auth.api.service';
import { GlobalErrorService } from '@services/GlobalErrorService/global-error.service';
import { GlobalErrorType } from '@models/GlobalErrorPayload';
import { AuthenticationService } from '@services/AuthService/auth.service';
import { HANDLES_CLIENT_ERRORS, HANDLES_SERVER_ERRORS } from '@core/http/http-context.tokens';


export const APICallInterceptor: HttpInterceptorFn = (req, next) => {
    const authAPIService = inject(AuthAPIService);
    const authService = inject(AuthenticationService);
    const globalErrorService = inject(GlobalErrorService);
    const isFormData = req.body instanceof FormData;
    const handlesClientErrors = req.context.get(HANDLES_CLIENT_ERRORS);
    const handlesServerErrors = req.context.get(HANDLES_SERVER_ERRORS);

    let headers = req.headers;

    const token = authService.token;

    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    if (!isFormData) headers = headers.set('Content-Type', 'application/json');

    const authRequest = req.clone({ headers });

    return next(authRequest).pipe(
        catchError((error) => {
            let message = '';

            // Callers that show 4xx messages inline get no toast for them. Session errors still do.
            const isClientError =
                error.status >= 400 && error.status < 500 && error.status !== 401 && error.status !== 440;
            const isServerError = error.status >= 500 || error.status === 0;
            const notify = (type: GlobalErrorType, status: number, title: string, detail: string) => {
                if (handlesClientErrors && isClientError) return;
                if (handlesServerErrors && isServerError) return;
                globalErrorService.emitError(type, status, title, detail);
            };

            switch (error.status) {
                case 400:
                    message = error.error?.message || 'An error occurred while processing your request.';
                    notify(GlobalErrorType.Alert, 400, 'Error', message);
                    break;

                case 401:
                    message = error.error?.message || 'You are not authorized to access this resource.';
                    notify(
                        GlobalErrorType.Alert,
                        401,
                        'Authentication Error',
                        message
                    );

                    if (authService.isLoggedIn) {
                        authAPIService.logout();
                    }

                    break;

                case 422:

                    if (error.error?.fieldError) {
                        const fieldError = error.error.fieldError;
                        const messages: string[] = [];

                        for (const key in fieldError) {
                            if (fieldError.hasOwnProperty(key)) {
                                messages.push(...fieldError[key]);
                            }
                        }

                        if (messages.length > 0) {
                            const formatted = messages.join('\n');
                            notify(GlobalErrorType.Alert, 422, 'Validation Failed', formatted);
                        } else {
                            notify(GlobalErrorType.Alert, 422, 'Validation Failed', 'Please check the form fields.');
                        }
                    }
                    else {
                        message = error.error?.message || 'Please check the form for errors.';
                        notify(GlobalErrorType.Alert, 422, 'Validation Error', message);
                    }

                    break;


                case 440:
                    message = error.error?.message || 'Your session has expired or access denied.';
                    notify(
                        GlobalErrorType.Alert,
                        440,
                        'Session Expired',
                        message
                    );
                    if (authService.isLoggedIn) {
                        authAPIService.logout();
                    }
                    break;

                case 500:
                    notify(
                        GlobalErrorType.Alert,
                        500,
                        'Server Error',
                        'An unexpected error occurred. Please try again later.'
                    );
                    break;

                case 0:
                    notify(
                        GlobalErrorType.Alert,
                        0,
                        'No Connection',
                        'You appear to be offline. Check your connection and try again.'
                    );
                    break;

                case 403:
                    notify(
                        GlobalErrorType.Alert,
                        403,
                        'Not Allowed',
                        error.error?.message || 'You do not have permission to do this. Ask an admin if you need access.'
                    );
                    break;

                case 404:
                    notify(
                        GlobalErrorType.Alert,
                        404,
                        'Not Found',
                        error.error?.message || 'This record no longer exists. It may have been deleted. Refresh the page.'
                    );
                    break;

                case 409:
                    notify(
                        GlobalErrorType.Alert,
                        409,
                        'Record Changed',
                        error.error?.message || 'This record changed since you opened it. Reload to see the latest version.'
                    );
                    break;

                case 429:
                    notify(
                        GlobalErrorType.Alert,
                        429,
                        'Too Many Requests',
                        'Please wait a moment and try again.'
                    );
                    break;

                case 502:
                case 503:
                case 504:
                    notify(
                        GlobalErrorType.Alert,
                        error.status,
                        'Server Unavailable',
                        'The server is not responding right now. Try again in a minute.'
                    );
                    break;

                default:
                    notify(
                        GlobalErrorType.Alert,
                        error.status,
                        'Unexpected Error',
                        error.error?.message || `Something went wrong (code ${error.status}). Try again; if it keeps happening, contact support.`
                    );
                    break;
            }

            return throwError(() => error);
        })
    );
};
